
import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import AlertBox from '../../Components/AlertBox';

// API Configuration
const API_BASE_URL = 'https://slateblue-mallard-430836.hostingersite.com/api';
const Companies = () => {
  const location = useLocation();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]); // New state for subscription plans
  const [editingCompanyId, setEditingCompanyId] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // const [showBranchPassword, setShowBranchPassword] = useState(false);
  const [formData, setFormData] = useState({
    businessName: '',
    businessEmail: '',
    businessPhone: '',
    EmergencyMobNo: '',
    password: '',
    businessSubscriptionPlan: '',
    businessCreatedDate: new Date().toISOString().split('T')[0],
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    weeklyHoliday: [],
    address: '',
    businessLogo: '',
    superadmin: '',
    franchise: '',
    menulimitaccess: {
      taskManagement: 10,
      leadManagement: 10,
      hrms: 10,
      support: 10,
      projectManagement: 10
    }
  });
  const [logoFile, setLogoFile] = useState(null); // New state for selected logo file
  const [logoPreview, setLogoPreview] = useState(null); // New state for image preview

  // New states for manual plan entry
  const [useManualPlan, setUseManualPlan] = useState(false);
  const [manualPlanData, setManualPlanData] = useState({
    title: '',
    startDate: new Date().toISOString().split('T')[0], // Today's date as YYYY-MM-DD
    endDate: '', // Date picker format: YYYY-MM-DD
    price: '',
    userLimits: {
      taskManagement: '',
      leadManagement: '',
      hrms: '',
      support: '',
      projectManagement: ''
    },
    accessPermissions: {
      taskManagement: false,
      leadManagement: false,
      hrms: false,
      support: false,
      projectManagement: false
    }
  });

  const formRef = useRef(null); // NEW: Ref for the form container

  const [currentUser, setCurrentUser] = useState(null); // New state for current user role and ID
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        console.log('Decoded JWT token:', decoded);
        setCurrentUser({
          id: decoded.id,
          userId: decoded.id,
          role: decoded.role,
          companyId: decoded.companyId || null,
          accessPermissions: decoded.accessPermissions || [],
          superadmin: decoded.superadmin || null,  // Check if this exists
          franchise: decoded.franchise || null
        });
      } catch (error) {
        console.error('Error decoding JWT:', error);
      }
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, []);

  // Fetch subscription plans after currentUser is set
  useEffect(() => {
    if (currentUser) {
      fetchSubscriptionPlans();
    }
  }, [currentUser]);

  // Handle edit mode when navigated from FranchiseCompanies page
  useEffect(() => {
    if (location.state?.editingCompany && currentUser?.role && ['superadmin', 'super_employee'].includes(currentUser.role)) {
      const company = location.state.editingCompany;
      handleEditCompany(company);
      // Clear the location state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state, currentUser]);

  const fetchSubscriptionPlans = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No access token for fetching subscription plans');
      setSubscriptionPlans([]);
      return;
    }

    try {
      console.log("Attempting to fetch subscription plans for role:", currentUser?.role);

      // Determine which endpoint to use based on user role
      if (currentUser && currentUser.role === 'franchise') {
        // Franchise users: Use hardcoded dummy plans (same as Subscription page)
        const dummyPlans = [
          {
            _id: 'sample1',
            title: '7-Day Trial',
            userLimits: {
              taskManagement: 10,
              leadManagement: 10,
              hrms: 10,
              support: 10,
              projectManagement: 10
            },
            duration: '7-day',
            price: 0,
            accessPermissions: {
              taskManagement: true,
              leadManagement: true,
              hrms: true,
              support: true,
              projectManagement: true
            },
          },
          {
            _id: 'sample2',
            title: '15-Day Pro Trial',
            userLimits: {
              taskManagement: 20,
              leadManagement: 20,
              hrms: 20,
              support: 20,
              projectManagement: 20
            },
            duration: '15-day',
            price: 0,
            accessPermissions: {
              taskManagement: true,
              leadManagement: true,
              hrms: true,
              support: true,
              projectManagement: true
            },
          },
        ];

        setSubscriptionPlans(dummyPlans);
        console.log(`Franchise user: loaded ${dummyPlans.length} hardcoded subscription plans`);

      } else {
        // Superadmin and super_employee users: Fetch plans from original endpoint (existing plans)
        const response = await axios.get(`${API_BASE_URL}/subscription-plans`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        console.log("Fetched superadmin/super_employee subscription plans:", response.data);

        // Extract the 'plans' array from the response object (original format)
        const subscriptionData = response.data.plans || [];

        // Normalize plans for backward compatibility
        const normalizedPlans = subscriptionData.map(plan => {
          if (plan.access && !plan.accessPermissions) {
            return {
              ...plan,
              accessPermissions: {
                taskManagement: !!plan.access.task,
                leadManagement: !!plan.access.lead,
                hrms: !!plan.access.hrms,
                support: !!plan.access.support,
                projectManagement: !!plan.access.projectManagement,
              },
              userLimits: plan.userLimits || {
                taskManagement: plan.userLimit || 0,
                leadManagement: plan.userLimit || 0,
                hrms: plan.userLimit || 0,
                support: plan.userLimit || 0,
                projectManagement: plan.userLimit || 0,
              }
            };
          }
          return plan;
        });

        setSubscriptionPlans(normalizedPlans);
        console.log(`Superadmin/Super Employee: loaded ${normalizedPlans.length} subscription plans`);
      }

    } catch (err) {
      console.error("Error fetching subscription plans:", err.response?.data || err.message || err);
      setSubscriptionPlans([]); // Fallback to empty array on error
    }
  };


  const getPlanName = (planData) => {
    if (!planData) return 'No Plan';

    // Helper to lookup plan by ID
    const findPlanTitleById = (id) => {
      const plan = subscriptionPlans.find(p => p._id === id);
      return plan ? plan.title : null;
    };

    // If it's a string that looks like JSON, try to parse it
    if (typeof planData === 'string' && planData.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(planData);
        if (parsed) {
          if (parsed.title) return parsed.title;
          if (parsed._id) {
            const title = findPlanTitleById(parsed._id);
            if (title) return title;
            // Fallback: return ID if title not found, but cleaner than JSON
            return `Plan ID: ${parsed._id.slice(-6)}`;
          }
        }
      } catch (e) {
        // Not valid JSON, treat as a plain string
      }
    }

    // If it's a plan object (manual plan or enriched plan from dropdown) with a title property
    if (typeof planData === 'object' && planData !== null) {
      if (planData.title) return planData.title;
      if (planData._id) {
        const title = findPlanTitleById(planData._id);
        if (title) return title;
      }
    }

    // If it's a string ID, look up in subscriptionPlans array
    if (typeof planData === 'string') {
      // Check if it's a MongoDB ObjectId (24 hex chars)
      if (planData.match(/^[0-9a-fA-F]{24}$/)) {
        const title = findPlanTitleById(planData);
        return title || 'Unknown Plan';
      } else {
        // Otherwise it's a plain text name (backward compatibility)
        return planData;
      }
    }

    return 'No Plan';
  };

  // Add this for consistent styling
  const getPlanDisplayInfo = (planId) => {
    const planName = getPlanName(planId);

    let colorClass = 'bg-gray-100 text-gray-800'; // default
    if (planName.toLowerCase().includes('enterprise')) {
      colorClass = 'bg-purple-100 text-purple-800';
    } else if (planName.toLowerCase().includes('premium')) {
      colorClass = 'bg-blue-100 text-blue-800';
    } else if (planName.toLowerCase().includes('standard')) {
      colorClass = 'bg-green-100 text-green-800';
    } else if (planName.toLowerCase().includes('basic')) {
      colorClass = 'bg-yellow-100 text-yellow-800';
    }

    return { name: planName, colorClass };
  };

  // Compute an end date based on a duration label like 'Monthly' or 'Yearly' or '6 months'
  const computeEndDateFromDuration = (startDateIso, durationLabel) => {
    if (!startDateIso) startDateIso = new Date().toISOString();
    const start = new Date(startDateIso);
    if (!durationLabel) return start.toISOString();

    const label = String(durationLabel).toLowerCase();

    // Numeric days like '7 days', '7-day', '15-day', or '30 days'
    const daysMatch = label.match(/(\d+)[-\s]*day/);
    if (daysMatch) {
      const days = parseInt(daysMatch[1], 10);
      const end = new Date(start);
      end.setDate(end.getDate() + days);
      return end.toISOString();
    }

    // Yearly
    if (label.includes('year')) {
      const end = new Date(start);
      end.setFullYear(end.getFullYear() + 1);
      return end.toISOString();
    }

    // Monthly
    if (label.includes('month')) {
      const end = new Date(start);
      end.setMonth(end.getMonth() + 1);
      return end.toISOString();
    }

    // Numeric months like '6 months' or '3 months'
    const monthsMatch = label.match(/(\d+)[-\s]*month/);
    if (monthsMatch) {
      const months = parseInt(monthsMatch[1], 10);
      const end = new Date(start);
      end.setMonth(end.getMonth() + months);
      return end.toISOString();
    }

    // Numeric years like '2 years'
    const yearsMatch = label.match(/(\d+)[-\s]*year/);
    if (yearsMatch) {
      const years = parseInt(yearsMatch[1], 10);
      const end = new Date(start);
      end.setFullYear(end.getFullYear() + years);
      return end.toISOString();
    }

    return start.toISOString();
  };

  // Given start/end ISO strings, generate a human friendly duration label
  const computeDurationLabelFromDates = (startIso, endIso) => {
    if (!startIso || !endIso) return '';
    const start = new Date(startIso);
    const end = new Date(endIso);
    const diffMs = end - start;
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays >= 365) return 'Yearly';
    if (diffDays >= 28) return 'Monthly';
    return `${diffDays} days`;
  };

  const fetchCompanies = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('No access token found. Please log in.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Fetched companies:", response.data);
      // Ensure response.data is an array
      const companiesData = Array.isArray(response.data) ? response.data : [];
      setCompanies(companiesData);
    } catch (err) {
      console.error("Error fetching companies:", err.response || err.message || err);
      setError('Failed to fetch companies. You might not be authorized.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'createBranch') {
      setFormData((prevFormData) => ({
        ...prevFormData,
        createBranch: checked,
      }));
    } else if (name.startsWith('branch.')) {
      const branchField = name.split('.')[1];

      if (branchField === 'weeklyHoliday') {
        // Handle branch holiday checkbox toggle
        setFormData((prevFormData) => {
          const currentHolidays = Array.isArray(prevFormData.branchData.weeklyHoliday)
            ? prevFormData.branchData.weeklyHoliday
            : []; // Fallback to empty array if undefined
          if (checked) {
            return {
              ...prevFormData,
              branchData: {
                ...prevFormData.branchData,
                weeklyHoliday: [...currentHolidays, value]
              }
            };
          } else {
            return {
              ...prevFormData,
              branchData: {
                ...prevFormData.branchData,
                weeklyHoliday: currentHolidays.filter(day => day !== value)
              }
            };
          }
        });
      } else setFormData((prevFormData) => ({
        ...prevFormData,
        branchData: {
          ...prevFormData.branchData,
          [branchField]: value,
        },
      }));
    } else if (name.startsWith('menulimitaccess.')) {
      const limitField = name.split('.')[1];
      setFormData((prevFormData) => ({
        ...prevFormData,
        menulimitaccess: {
          ...prevFormData.menulimitaccess,
          [limitField]: type === 'number' ? Number(value) : value,
        },
      }));
    } else if (name === 'weeklyHoliday') {
      // Handle holiday checkbox toggle
      setFormData((prevFormData) => {
        const currentHolidays = Array.isArray(prevFormData.weeklyHoliday)
          ? prevFormData.weeklyHoliday
          : (prevFormData.weeklyHoliday ? JSON.parse(prevFormData.weeklyHoliday) : []);
        if (checked) {
          return { ...prevFormData, weeklyHoliday: [...currentHolidays, value] };
        } else {
          return { ...prevFormData, weeklyHoliday: currentHolidays.filter(day => day !== value) };
        }
      });
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };


  const handleAddCompany = async (e) => {
    e.preventDefault();

    // Check if current user is loaded and authorized (superadmin, super_employee, or franchise)
    if (!currentUser || !['superadmin', 'super_employee', 'franchise'].includes(currentUser.role)) {
      setShowAlert('❌ You do not have permission to create companies. Only superadmins and franchise users can create companies.');
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      setShowAlert('Access token not found. Please login.');
      return;
    }

    // Enhanced validation (removed superadmin as it's auto-set)
    const requiredFields = {
      businessName: 'Business Name',
      businessEmail: 'Business Email',
      businessPhone: 'Company Mobile',
      password: 'Password'
    };

    const missingFields = Object.entries(requiredFields)
      .filter(([field]) => !formData[field] || formData[field].trim() === '')
      .map(([_, label]) => label);

    if (missingFields.length > 0) {
      setShowAlert(`❌ Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.businessEmail)) {
      setShowAlert('❌ Please enter a valid email address');
      return;
    }

    const weeklyHolidayArr = formData.weeklyHoliday && formData.weeklyHoliday.length > 0
      ? formData.weeklyHoliday
      : ['Sunday']; // Default to Sunday if empty

    // Create FormData for multipart upload
    const formDataToSend = new FormData();
    formDataToSend.append('businessName', formData.businessName.trim());
    formDataToSend.append('businessEmail', formData.businessEmail.trim().toLowerCase());
    formDataToSend.append('password', formData.password);
    formDataToSend.append('businessCreatedDate', formData.businessCreatedDate
      ? new Date(formData.businessCreatedDate).toISOString()
      : new Date().toISOString());
    if (formData.startDate) {
      formDataToSend.append('startDate', new Date(formData.startDate).toISOString());
    }
    if (formData.endDate) {
      formDataToSend.append('endDate', new Date(formData.endDate).toISOString());
    }
    formDataToSend.append('weeklyHoliday', JSON.stringify(weeklyHolidayArr)); // Array as JSON string

    // Auto-set superadmin or franchise based on current user role
    // Auto-set superadmin or franchise based on current user role
    if (currentUser.role === 'superadmin' || currentUser.role === 'super_employee') {
      // For super_employee, you need to get the superadminid from the token or user data
      // Assuming the superadminid is stored in the token as 'superadminid'
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const superadminId = decoded.superadminid || decoded.superadmin || currentUser.id;
          formDataToSend.append('superadmin', superadminId);
        } catch (error) {
          console.error('Error decoding token for superadminid:', error);
          formDataToSend.append('superadmin', currentUser.id);
        }
      }
    } else if (currentUser.role === 'franchise') {
      formDataToSend.append('franchise', currentUser.id);
    } else if (currentUser.role === 'super_employee') {
      // For super employees, use their associated superadmin ID from the token
      const token = localStorage.getItem('authToken');
      const decoded = jwtDecode(token);
      if (decoded.superadmin) {
        formDataToSend.append('superadmin', decoded.superadmin);
      }
    } else if (currentUser.role === 'franchise') {
      formDataToSend.append('franchise', currentUser.id); // Set franchise ID
    }

    // Add optional fields
    if (formData.businessPhone && formData.businessPhone.trim()) {
      formDataToSend.append('businessPhone', formData.businessPhone.trim());
    }
    if (formData.EmergencyMobNo && formData.EmergencyMobNo.trim()) {
      formDataToSend.append('EmergencyMobNo', formData.EmergencyMobNo.trim());
    }
    if (formData.businessSubscriptionPlan) {
      // Send the entire plan object, not just the ID
      const planData = typeof formData.businessSubscriptionPlan === 'object'
        ? formData.businessSubscriptionPlan
        : { _id: formData.businessSubscriptionPlan };
      formDataToSend.append('businessSubscriptionPlan', JSON.stringify(planData));
    }
    if (formData.address && formData.address.trim()) {
      formDataToSend.append('address', formData.address.trim());
    }
    if (formData.menulimitaccess) {
      formDataToSend.append('menulimitaccess', JSON.stringify(formData.menulimitaccess));
    }
    if (logoFile) {
      formDataToSend.append('businessLogo', logoFile); // Append file for upload
    }

    console.log('Form data before submission:', formData);
    console.log('menulimitaccess values:', formData.menulimitaccess);

    try {
      console.log("Sending create company request with FormData");

      const response = await axios.post(
        `${API_BASE_URL}/companies`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data', // Required for file upload
          },
        }
      );

      console.log("Company created successfully:", response.data);
      setShowAlert('✅ Company created successfully!');

      fetchCompanies();
      resetForm();
    } catch (error) {
      console.error("❌ Error creating company:", error.response?.data || error.message || error);

      // Enhanced error handling
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;

        switch (status) {
          case 400:
            const errorMessage = errorData.message || errorData.error || 'Invalid data provided';
            setShowAlert(`❌ Validation Error: ${errorMessage}`);
            console.error("Detailed error:", errorData);
            break;
          case 401:
            setShowAlert('❌ Authentication failed. Please login again.');
            break;
          case 403:
            setShowAlert('❌ Access denied. You do not have permission to create companies.');
            break;
          case 409:
            setShowAlert('❌ Company with this email already exists.');
            break;
          case 500:
            setShowAlert('❌ Server error. Please try again later.');
            break;
          default:
            setShowAlert(`❌ Error creating company: ${errorData.message || 'Unknown error'}`);
        }
      } else if (error.request) {
        setShowAlert('❌ Network error. Please check your internet connection.');
      } else {
        setShowAlert(`❌ Error: ${error.message}`);
      }
    }
  };



  const handleEditCompany = (company) => {
    setIsEditing(true);
    setEditingCompanyId(company._id);
    setLogoFile(null); // Clear new file
    setLogoPreview(company.businessLogo || null); // Set preview from existing URL

    // Fetch fresh company data from backend
    const fetchCompanyDetails = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setShowAlert('Access token not found. Please login.');
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/companies/${company._id}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        const fetchedCompany = response.data;
        console.log('Fetched company details:', fetchedCompany);

        // Determine if the plan is manually set (has fields like duration, price, accessPermissions) or from dropdown
        let useManual = false;
        let planData = {
          title: '',
          startDate: new Date().toISOString().split('T')[0],
          endDate: '',
          price: '',
          userLimit: '',
          accessPermissions: {
            taskManagement: false,
            leadManagement: false,
            hrms: false,
            support: false,
            projectManagement: false
          }
        };

        // Normalize businessSubscriptionPlan: it may be an object or a JSON string (or an ID)
        let storedPlan = fetchedCompany.businessSubscriptionPlan;
        if (storedPlan && typeof storedPlan === 'string') {
          try {
            const parsed = JSON.parse(storedPlan);
            // Only replace if parsing produced an object
            if (parsed && typeof parsed === 'object') storedPlan = parsed;
          } catch (e) {
            // not JSON — could be just an ID or a display string; keep as-is
          }
        }

        // Check if businessSubscriptionPlan is an object with custom/manual fields
        if (storedPlan && typeof storedPlan === 'object') {
          const plan = storedPlan;
          // Treat as manual plan when it has accessPermissions, userLimits or explicit start/end dates or price
          const looksManual = plan.accessPermissions || plan.userLimits || (plan.startDate && plan.endDate) || plan.price;
          if (looksManual) {
            useManual = true;
            planData = {
              title: plan.title || '',
              startDate: plan.startDate ? new Date(plan.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
              endDate: plan.endDate ? new Date(plan.endDate).toISOString().split('T')[0] : '',
              price: plan.price || '',
              userLimits: plan.userLimits || {
                taskManagement: plan.userLimit || '',
                leadManagement: plan.userLimit || '',
                hrms: plan.userLimit || '',
                support: plan.userLimit || '',
                projectManagement: plan.userLimit || ''
              },
              accessPermissions: plan.accessPermissions || {
                taskManagement: false,
                leadManagement: false,
                hrms: false,
                support: false,
                projectManagement: false
              }
            };
          }
        }

        setUseManualPlan(useManual);
        setManualPlanData(planData);

        setFormData({
          businessName: fetchedCompany.businessName || '',
          businessEmail: fetchedCompany.businessEmail || '',
          businessPhone: fetchedCompany.businessPhone || '',
          EmergencyMobNo: fetchedCompany.EmergencyMobNo || '',
          password: '', // Password is not pre-filled for security
          businessSubscriptionPlan: (typeof fetchedCompany.businessSubscriptionPlan === 'string' && storedPlan && typeof storedPlan === 'object') ? storedPlan : fetchedCompany.businessSubscriptionPlan || '',
          businessCreatedDate: fetchedCompany.businessCreatedDate
            ? new Date(fetchedCompany.businessCreatedDate).toISOString().split('T')[0]
            : '',
          weeklyHoliday: Array.isArray(fetchedCompany.weeklyHoliday)
            ? fetchedCompany.weeklyHoliday
            : (fetchedCompany.weeklyHoliday ? JSON.parse(fetchedCompany.weeklyHoliday) : []),
          address: fetchedCompany.address || '',
          businessLogo: fetchedCompany.businessLogo || '', // Load existing URL
          superadmin: fetchedCompany.superadmin || '',
          franchise: fetchedCompany.franchise || '',
          menulimitaccess: fetchedCompany.menulimitaccess || {
            taskManagement: 0,
            leadManagement: 0,
            hrms: 0,
            support: 0,
            projectManagement: 0
          },
          createBranch: false,
          branchData: {
            businessName: '',
            businessEmail: '',
            businessPhone: '',
            address: '',
            password: '',
            weeklyHoliday: [],
          }
        });
        setShowForm(true);
        setTimeout(() => {
          formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } catch (error) {
        console.error('Error fetching company details:', error);
        setShowAlert('Failed to fetch company details. Please try again.');
      }
    };

    // Call the fetch function
    fetchCompanyDetails();
  };



  const handleUpdateCompany = async (e) => {
    e.preventDefault();

    // Check authorization for updates (same as create)
    if (!currentUser || !['superadmin', 'super_employee', 'franchise'].includes(currentUser.role)) {
      setShowAlert('❌ You do not have permission to update companies. Only superadmins, super employees, and franchise users can update companies.');
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      setShowAlert('Access token not found. Please login.');
      return;
    }

    const weeklyHolidayArr = formData.weeklyHoliday && formData.weeklyHoliday.length > 0
      ? formData.weeklyHoliday
      : [];

    // Create FormData for multipart upload
    const formDataToSend = new FormData();
    formDataToSend.append('businessName', formData.businessName);
    formDataToSend.append('businessEmail', formData.businessEmail);
    if (formData.businessCreatedDate) {
      formDataToSend.append('businessCreatedDate', new Date(formData.businessCreatedDate).toISOString());
    }
    formDataToSend.append('weeklyHoliday', JSON.stringify(weeklyHolidayArr));

    // Preserve existing superadmin/franchise from formData (loaded during edit)
    if (formData.superadmin) {
      formDataToSend.append('superadmin', formData.superadmin);
    }
    if (formData.franchise) {
      formDataToSend.append('franchise', formData.franchise);
    }

    // Add optional fields if provided
    if (formData.businessPhone) {
      formDataToSend.append('businessPhone', formData.businessPhone);
    }
    if (formData.EmergencyMobNo) {
      formDataToSend.append('EmergencyMobNo', formData.EmergencyMobNo);
    }
    if (formData.businessSubscriptionPlan) {
      // Send the entire plan object, not just the ID
      const planData = typeof formData.businessSubscriptionPlan === 'object'
        ? formData.businessSubscriptionPlan
        : { _id: formData.businessSubscriptionPlan };
      formDataToSend.append('businessSubscriptionPlan', JSON.stringify(planData));
    }
    if (formData.address) {
      formDataToSend.append('address', formData.address);
    }
    if (formData.menulimitaccess) {
      formDataToSend.append('menulimitaccess', JSON.stringify(formData.menulimitaccess));
    }
    if (formData.password) {
      formDataToSend.append('password', formData.password); // Only if changed
    }
    if (logoFile) {
      formDataToSend.append('businessLogo', logoFile); // New file upload
    } else if (formData.businessLogo) {
      // Keep existing URL if no new file
      formDataToSend.append('businessLogo', formData.businessLogo);
    }

    try {
      console.log("Sending update company request with FormData");

      const response = await axios.put(
        `${API_BASE_URL}/companies/${editingCompanyId}`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data', // Required for file upload
          },
        }
      );

      console.log("Company updated successfully:", response.data);
      setShowAlert('✅ Company updated successfully!');
      fetchCompanies();
      resetForm();
    } catch (error) {
      console.error("Error updating company:", error.response?.data || error.message || error);
      setShowAlert('Error updating company. Check console for details.');
    }
  };


  // New handler for logo file selection
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type (images only)
      if (!file.type.startsWith('image/')) {
        setShowAlert('❌ Please select a valid image file (JPG, PNG, etc.).');
        e.target.value = ''; // Clear input
        return;
      }
      // Validate file size (e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setShowAlert('❌ File size must be less than 5MB.');
        e.target.value = '';
        return;
      }
      setLogoFile(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
      // Update formData with  filename or empty (backend handles upload)
      setFormData(prev => ({ ...prev, businessLogo: '' })); // Clear existing URL for new upload
    } else {
      setLogoFile(null);
      setLogoPreview(null);
      setFormData(prev => ({ ...prev, businessLogo: '' }));
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('Are you sure you want to delete this company? This action cannot be undone.')) {
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      setShowAlert('Access token not found. Please login.');
      return;
    }

    try {
      console.log("Sending delete company request for ID:", companyId);

      await axios.delete(
        `${API_BASE_URL}/companies/${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Company deleted successfully");
      fetchCompanies(); // Refresh the list
    } catch (error) {
      console.error("Error deleting company:", error.response?.data || error.message || error);
      setShowAlert('Error deleting company. Check console for details.');
    }
  };

  const resetForm = () => {
    setFormData({
      businessName: '',
      businessEmail: '',
      businessPhone: '',
      EmergencyMobNo: '',
      password: '',
      businessSubscriptionPlan: '',
      businessCreatedDate: new Date().toISOString().split('T')[0],
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      weeklyHoliday: [],
      address: '',
      businessLogo: '',
      superadmin: '',
      franchise: '',
      menulimitaccess: {
        taskManagement: 10,
        leadManagement: 10,
        hrms: 10,
        support: 10,
        projectManagement: 10
      }
    });

    setLogoFile(null);
    setLogoPreview(null);
    setUseManualPlan(false);
    setManualPlanData({
      title: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      price: '',
      userLimits: {
        taskManagement: '',
        leadManagement: '',
        hrms: '',
        support: '',
        projectManagement: ''
      },
      accessPermissions: {
        taskManagement: false,
        leadManagement: false,
        hrms: false,
        support: false,
        projectManagement: false
      }
    });
    setShowForm(false);
    setIsEditing(false);
    setEditingCompanyId(null);
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading companies...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
          <div className="text-red-500 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-medium">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Company Management</h1>
            <p className="text-gray-600 mt-2">Manage all companies in your system</p>
          </div>
          {currentUser && ['superadmin', 'super_employee', 'franchise'].includes(currentUser.role) ? (
            <button
              onClick={() => {
                if (showForm) resetForm();
                else setShowForm(true);
              }}
              className="mt-4 md:mt-0 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-md hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 flex items-center"
            >
              {showForm ? (
                // ❌ Cancel icon
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                // ž• Add icon
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              {showForm ? 'Cancel' : 'Add Company'}
            </button>

          ) : (
            <div className="mt-4 md:mt-0 text-red-600 font-medium">
              ❌ You do not have permission to create companies. Contact a superadmin.
            </div>
          )}
        </div>


        {showForm && currentUser && ['superadmin', 'super_employee', 'franchise'].includes(currentUser.role) ? (
          <div ref={formRef} className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">{isEditing ? 'Edit Company' : 'Add New Company'}</h2>
            <form onSubmit={isEditing ? handleUpdateCompany : handleAddCompany} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                  <input
                    type="text"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Email *</label>
                  <input
                    type="email"
                    name="businessEmail"
                    value={formData.businessEmail}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="business@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Mobile *</label>
                  <input
                    type="text"
                    name="businessPhone"
                    value={formData.businessPhone}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow only numbers and restrict length to 10
                      if (value.length <= 10) {
                        handleInputChange(e);
                      }
                    }}
                    onInput={(e) => {
                      // Remove any non-digit characters (for safety)
                      e.target.value = e.target.value.replace(/\D/g, '');
                    }}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter 10-digit mobile number"
                    min="0"
                    inputMode="numeric"
                  />

                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Mobile</label>
                  <input
                    type="text"
                    name="EmergencyMobNo"
                    value={formData.EmergencyMobNo}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow only numbers and restrict length to 10
                      if (value.length <= 10) {
                        handleInputChange(e);
                      }
                    }}
                    onInput={(e) => {
                      // Remove any non-digit characters
                      e.target.value = e.target.value.replace(/\D/g, '');
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Emergency contact number"
                    min="0"
                    inputMode="numeric"
                  />

                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password {isEditing ? '' : '*'}
                    {!isEditing && (
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="ml-2 text-xs text-indigo-600 hover:text-indigo-800 focus:outline-none"
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required={!isEditing}
                      disabled={isEditing} // Disable password field when editing
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder={isEditing ? 'Password cannot be changed' : 'Set a password'}
                    />
                    {!isEditing && (
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                      >
                        {showPassword ? (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    )}
                  </div>
                  {isEditing && (
                    <p className="text-xs text-gray-500 mt-1">
                      Password cannot be changed after creation for security reasons.
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                {currentUser && (currentUser.role === 'superadmin' || currentUser.role === 'super_employee' || currentUser.role === 'franchise') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Plan</label>
                    <div className={currentUser.role === 'franchise' ? 'mb-3' : 'flex gap-2 mb-3'}>
                      <select
                        name="businessSubscriptionPlan"
                        value={formData.businessSubscriptionPlan ? (typeof formData.businessSubscriptionPlan === 'string' ? formData.businessSubscriptionPlan : formData.businessSubscriptionPlan._id) : ''}
                        onChange={(e) => {
                          const selectedPlan = subscriptionPlans.find(plan => plan._id === e.target.value);
                          if (selectedPlan) {
                            const startDateIso = new Date().toISOString();
                            const endDateIso = computeEndDateFromDuration(startDateIso, selectedPlan.duration || selectedPlan.durationLabel || selectedPlan.title);

                            // Construct the plan object explicitly to match manual plan format
                            const planWithDates = {
                              _id: selectedPlan._id,
                              title: selectedPlan.title || selectedPlan.planName || 'Unknown Plan',
                              price: selectedPlan.price ? String(selectedPlan.price) : '0',
                              userLimits: selectedPlan.userLimits || {
                                taskManagement: selectedPlan.userLimit || 0,
                                leadManagement: selectedPlan.userLimit || 0,
                                hrms: selectedPlan.userLimit || 0,
                                support: selectedPlan.userLimit || 0,
                                projectManagement: selectedPlan.userLimit || 0
                              },
                              accessPermissions: {
                                taskManagement: selectedPlan.access?.task ?? selectedPlan.accessPermissions?.taskManagement ?? false,
                                leadManagement: selectedPlan.access?.lead ?? selectedPlan.accessPermissions?.leadManagement ?? false,
                                hrms: selectedPlan.access?.hrms ?? selectedPlan.accessPermissions?.hrms ?? false,
                                support: selectedPlan.access?.support ?? selectedPlan.accessPermissions?.support ?? false,
                                projectManagement: selectedPlan.access?.projectManagement ?? selectedPlan.accessPermissions?.projectManagement ?? false
                              },
                              startDate: startDateIso,
                              endDate: endDateIso
                            };

                            // Also update formData startDate and endDate fields
                            const startDateFormatted = new Date(startDateIso).toISOString().split('T')[0];
                            const endDateFormatted = new Date(endDateIso).toISOString().split('T')[0];

                            // Set menulimitaccess from the selected plan
                            const menuLimitAccess = {
                              taskManagement: selectedPlan.userLimits?.taskManagement ?? selectedPlan.userLimit ?? 0,
                              leadManagement: selectedPlan.userLimits?.leadManagement ?? selectedPlan.userLimit ?? 0,
                              hrms: selectedPlan.userLimits?.hrms ?? selectedPlan.userLimit ?? 0,
                              support: selectedPlan.userLimits?.support ?? selectedPlan.userLimit ?? 0,
                              projectManagement: selectedPlan.userLimits?.projectManagement ?? selectedPlan.userLimit ?? 0
                            };

                            setFormData(prevFormData => ({
                              ...prevFormData,
                              businessSubscriptionPlan: planWithDates,
                              menulimitaccess: menuLimitAccess,
                              startDate: startDateFormatted,
                              endDate: endDateFormatted
                            }));
                          } else {
                            setFormData(prevFormData => ({ ...prevFormData, businessSubscriptionPlan: '' }));
                          }
                          setUseManualPlan(false);
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        disabled={useManualPlan}
                      >
                        <option value="">Select a plan</option>
                        {subscriptionPlans.map((plan) => (
                          <option key={plan._id} value={plan._id}>
                            {plan.title}
                          </option>
                        ))}
                      </select>
                      {(currentUser.role === 'superadmin' || currentUser.role === 'super_employee') && (
                        <button
                          type="button"
                          onClick={() => {
                            setUseManualPlan(!useManualPlan);
                            if (!useManualPlan) {
                              setFormData(prevFormData => ({
                                ...prevFormData,
                                businessSubscriptionPlan: ''
                              }));
                            }
                          }}
                          className={`px-4 py-2 rounded-md font-medium transition-colors ${useManualPlan
                            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                            : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                            }`}
                        >
                          {useManualPlan ? '✅Manual' : 'Set Manually'}
                        </button>
                      )}
                    </div>

                    {/* Manual Plan Entry Form */}
                    {useManualPlan && (
                      <div className="border border-indigo-300 bg-indigo-50 rounded-md p-4 space-y-4">
                        <h3 className="font-semibold text-gray-800 text-sm">Set Plan Manually</h3>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Plan Title</label>
                          <input
                            type="text"
                            value={manualPlanData.title}
                            onChange={(e) => setManualPlanData(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="e.g., Premium Plan"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                          <input
                            type="date"
                            value={manualPlanData.startDate}
                            onChange={(e) => setManualPlanData(prev => ({ ...prev, startDate: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                          <input
                            type="date"
                            value={manualPlanData.endDate}
                            onChange={(e) => setManualPlanData(prev => ({ ...prev, endDate: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                          <input
                            type="number"
                            value={manualPlanData.price}
                            onChange={(e) => setManualPlanData(prev => ({ ...prev, price: e.target.value }))}
                            placeholder="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Access Permissions & User Limits</label>
                          <div className="space-y-3">
                            {[
                              { key: 'taskManagement', label: 'Task Management' },
                              { key: 'leadManagement', label: 'Lead Management' },
                              { key: 'hrms', label: 'HRMS' },
                              { key: 'support', label: 'Support' },
                              { key: 'projectManagement', label: 'Project Management' }
                            ].map(permission => (
                              <div key={permission.key} className="flex items-center space-x-4 p-3 border border-gray-200 rounded-md">
                                <label className="flex items-center cursor-pointer flex-1">
                                  <input
                                    type="checkbox"
                                    checked={manualPlanData.accessPermissions[permission.key]}
                                    onChange={(e) => setManualPlanData(prev => ({
                                      ...prev,
                                      accessPermissions: {
                                        ...prev.accessPermissions,
                                        [permission.key]: e.target.checked
                                      }
                                    }))}
                                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                  />
                                  <span className="ml-2 text-sm text-gray-700 font-medium">{permission.label}</span>
                                </label>
                                <div className="flex items-center space-x-2">
                                  <label className="text-xs text-gray-500">User Limit:</label>
                                  <input
                                    type="number"
                                    value={manualPlanData.userLimits[permission.key]}
                                    onChange={(e) => setManualPlanData(prev => ({
                                      ...prev,
                                      userLimits: {
                                        ...prev.userLimits,
                                        [permission.key]: e.target.value
                                      }
                                    }))}
                                    placeholder="0"
                                    min="0"
                                    disabled={!manualPlanData.accessPermissions[permission.key]}
                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-400"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            if (manualPlanData.endDate && manualPlanData.price) {
                              const startDateIso = new Date(manualPlanData.startDate).toISOString();
                              const endDateIso = new Date(manualPlanData.endDate).toISOString();
                              const planToStore = {
                                title: manualPlanData.title || 'Custom Plan',
                                price: manualPlanData.price,
                                userLimits: manualPlanData.userLimits,
                                accessPermissions: manualPlanData.accessPermissions,
                                startDate: startDateIso,
                                endDate: endDateIso
                              };

                              // Also set menulimitaccess for the company
                              const menuLimitAccess = {
                                taskManagement: manualPlanData.userLimits.taskManagement || 0,
                                leadManagement: manualPlanData.userLimits.leadManagement || 0,
                                hrms: manualPlanData.userLimits.hrms || 0,
                                support: manualPlanData.userLimits.support || 0,
                                projectManagement: manualPlanData.userLimits.projectManagement || 0
                              };

                              setFormData(prevFormData => ({
                                ...prevFormData,
                                businessSubscriptionPlan: planToStore,
                                menulimitaccess: menuLimitAccess
                              }));
                              setUseManualPlan(false);
                            } else {
                              alert('Please fill in End Date and Price');
                            }
                          }}
                          className="w-full px-3 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors"
                        >
                          Save Plan
                        </button>
                      </div>
                    )}
                  </div>
                )}


                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Logo</label>
                  <input
                    type="file"
                    name="businessLogo"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  {logoPreview && (
                    <div className="mt-2">
                      <img
                        src={logoPreview}
                        alt="Logo Preview"
                        className="w-24 h-24 object-cover rounded-md border"
                      />
                      <p className="text-xs text-gray-500 mt-1">Preview of business Logo</p>
                    </div>
                  )}

                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Created Date</label>
                  <input
                    type="date"
                    name="businessCreatedDate"
                    value={formData.businessCreatedDate}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    readOnly={isEditing || currentUser?.role === 'franchise'}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${(isEditing || currentUser?.role === 'franchise') ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                  {(isEditing || currentUser?.role === 'franchise') && (
                    <p className="text-xs text-gray-500 mt-1">This field is automatically set and cannot be changed.</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    readOnly={currentUser?.role === 'franchise'}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${currentUser?.role === 'franchise' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                  {currentUser?.role === 'franchise' && (
                    <p className="text-xs text-gray-500 mt-1">This field is automatically set based on the selected plan.</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    min={formData.startDate || new Date().toISOString().split('T')[0]}
                    readOnly={currentUser?.role === 'franchise'}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${currentUser?.role === 'franchise' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                  {currentUser?.role === 'franchise' && (
                    <p className="text-xs text-gray-500 mt-1">This field is automatically calculated based on the selected plan duration.</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Menu Limit Access</label>
                  <div className="space-y-3 p-4 border border-gray-200 rounded-md bg-gray-50">
                    {[
                      { key: 'taskManagement', label: 'Task Management' },
                      { key: 'leadManagement', label: 'Lead Management' },
                      { key: 'hrms', label: 'HRMS' },
                      { key: 'support', label: 'Support' },
                      { key: 'projectManagement', label: 'Project Management' }
                    ].map(permission => {
                      // Check if the current plan allows this permission
                      const isAllowed = formData.businessSubscriptionPlan?.accessPermissions?.[permission.key] !== false;

                      return (
                        <div key={permission.key} className={`flex items-center justify-between p-2 bg-white rounded border ${!isAllowed ? 'opacity-50' : ''}`}>
                          <div className="flex items-center">
                            <div className={`w-2 h-2 rounded-full mr-2 ${isAllowed ? 'bg-green-500' : 'bg-red-500'}`}></div>
                            <span className={`text-sm font-medium ${isAllowed ? 'text-gray-700' : 'text-gray-400'}`}>
                              {permission.label}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <label className="text-xs text-gray-500">User Limit:</label>
                            <input
                              type="number"
                              name={`menulimitaccess.${permission.key}`}
                              value={formData.menulimitaccess[permission.key]}
                              onChange={handleInputChange}
                              min="0"
                              disabled={!isAllowed}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="0"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Weekly Holidays</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                      <label key={day} className="flex items-center">
                        <input
                          type="checkbox"
                          name="weeklyHoliday"
                          value={day}
                          checked={formData.weeklyHoliday.includes(day)}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">{day}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Company address"
                  />
                </div>

              </div>




              <div className="md:col-span-2 pt-4">
                <button
                  type="submit"
                  disabled={loading || useManualPlan}
                  title={useManualPlan ? "Please save the manual plan first" : ""}
                  className={`w-full md:w-auto px-6 py-3 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 ${useManualPlan
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 focus:ring-green-500'
                    }`}
                >
                  {isEditing ? 'Update Company' : 'Create Company'}
                </button>
              </div>
            </form>
          </div>
        ) : showForm ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8 text-center">
            <p className="text-red-600 font-medium">❌ Access denied. You cannot create or edit companies.</p>
            <button
              onClick={() => setShowForm(false)}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Close
            </button>
          </div>
        ) : null}


        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Company List</h2>
            <p className="text-gray-600 text-sm">{Array.isArray(companies) ? companies.length : 0} companies found</p>
          </div>

          {!Array.isArray(companies) || companies.length === 0 ? (
            <div className="p-12 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2m2-0h-2m0-3h.01M17 18h.01M14 18h.01" />
              </svg>
              <p className="mt-4 text-gray-600">No companies found. Add your first company to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">sr no</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company Name</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company Email</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company Mobile</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created Date</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription Plan</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.isArray(companies) && companies.map((company, index) => (
                    <tr key={company._id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{company.businessName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{company.businessEmail}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.businessPhone || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {company.businessCreatedDate ? new Date(company.businessCreatedDate).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${company.businessSubscriptionPlan
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                          }`}>
                          {getPlanName(company.businessSubscriptionPlan)}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEditCompany(company)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteCompany(company._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Alert Box for success/error messages */}
      {showAlert && (
        <AlertBox
          message={showAlert}
          onClose={() => setShowAlert(false)}
        />
      )}
    </div>
  );
};

export default Companies;

